import React, { useEffect, useState } from 'react';
import { Loader2, Maximize2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { fetchWorkflowTestContacts, runWorkflowTest } from '../../utils/workflowTestApi';
import WorkflowEditorPanelShell from './WorkflowEditorPanelShell';

const WorkflowRunTestPanel = ({
  isOpen,
  onClose,
  onExpandedChange,
  token,
  workflowId,
  agencyId,
  scope = 'agency',
  executionAvailable = true,
  width,
  overlayMode = false,
  onOverlayDismiss,
  activePanelType = 'run-test',
  chromeHeight,
}) => {
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [running, setRunning] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [resultError, setResultError] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setExpanded(false);
      onExpandedChange?.(false);
    }
  }, [isOpen, onExpandedChange]);

  useEffect(() => {
    onExpandedChange?.(expanded);
  }, [expanded, onExpandedChange]);

  useEffect(() => {
    if (!isOpen || !token) return;
    let cancelled = false;
    setLoadingContacts(true);
    setResultMessage('');
    setResultError('');

    fetchWorkflowTestContacts({ token, agencyId, scope })
      .then((rows) => {
        if (!cancelled) setContacts(rows);
      })
      .catch(() => {
        if (!cancelled) setContacts([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingContacts(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, token, agencyId, scope]);

  const handleRunTest = async () => {
    if (!selectedContactId || !executionAvailable || !workflowId || workflowId === 'new') return;
    setRunning(true);
    setResultMessage('');
    setResultError('');
    try {
      const data = await runWorkflowTest({
        token,
        workflowId,
        contactId: selectedContactId,
        agencyId,
      });
      setResultMessage(data.message || `Test run started${data.execution_id ? ` (${data.execution_id})` : ''}.`);
    } catch (err) {
      const message = err?.response?.data?.detail
        || err?.response?.data?.error
        || err?.message
        || 'Test execution is not available yet.';
      setResultError(typeof message === 'string' ? message : 'Failed to run workflow test.');
    } finally {
      setRunning(false);
    }
  };

  if (!isOpen) return null;

  const canRun = Boolean(
    selectedContactId
    && executionAvailable
    && workflowId
    && workflowId !== 'new'
    && !running,
  );

  const panelWidth = width ?? (expanded ? 720 : 360);

  return (
    <WorkflowEditorPanelShell
      isOpen={isOpen}
      width={panelWidth}
      overlayMode={overlayMode}
      chromeHeight={chromeHeight}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      className="border-l border-slate-200 bg-white"
      testId="workflow-run-test-panel"
    >
      <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
        <h2 className="text-lg font-semibold text-slate-900">Run a test</h2>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Expand panel"
          >
            <Maximize2 size={16} />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            data-testid="workflow-run-test-close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-slate-800">Select contacts</Label>
          {loadingContacts ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 size={14} className="animate-spin" />
              Loading contacts...
            </div>
          ) : (
            <Select value={selectedContactId} onValueChange={setSelectedContactId}>
              <SelectTrigger data-testid="workflow-run-test-contact-select">
                <SelectValue placeholder="Select contact" />
              </SelectTrigger>
              <SelectContent>
                {contacts.length ? contacts.map((contact) => (
                  <SelectItem key={contact.id} value={String(contact.id)}>
                    {contact.label}
                  </SelectItem>
                )) : (
                  <SelectItem value="__empty" disabled>
                    No contacts available
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          )}
        </div>

        {!executionAvailable || workflowId === 'new' ? (
          <p className="text-sm text-slate-500">
            Save the workflow before running a test.
          </p>
        ) : null}

        {resultMessage ? (
          <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            {resultMessage}
          </p>
        ) : null}

        {resultError ? (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {resultError}
          </p>
        ) : null}
      </div>

      <div className="border-t border-slate-200 px-5 py-4">
        <Button
          onClick={handleRunTest}
          disabled={!canRun}
          className="w-full bg-emerald-600 hover:bg-emerald-700"
          data-testid="workflow-run-test-submit"
        >
          {running ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" />
              Running...
            </>
          ) : (
            'Run test'
          )}
        </Button>
      </div>
      </div>
    </WorkflowEditorPanelShell>
  );
};

export default WorkflowRunTestPanel;
